import { FetchOptions } from '../api/types';
import { AttachmentFilter, CommentFilter, RFIFilter, RfiFilterStatus } from '../types';

type FilterTuple = [string, string];
type FilterValue = string | number | boolean | string[];

const isEmptyArray = (array: unknown) => Array.isArray(array) && !array.length;

export const getFlatParams = (value: FilterValue): string =>
  Array.isArray(value) ? value.join(',') : value.toString();

const getEntityIdOrListFilterTuple = (property: string, value: FilterValue): [string, string] => [
  `filter[${property}]`,
  getFlatParams(value),
];

type Filter = Partial<RFIFilter & CommentFilter & AttachmentFilter>;
export const constructFilterParamList = (filter: Filter): FilterTuple[] => {
  const isRfiFilter = filter.id || filter.status;
  if (isRfiFilter) {
    return getParamList(handleRfiStatusFilter(filter));
  }

  return getParamList(filter);
};

const handleRfiStatusFilter = (filterBy: RFIFilter): RFIFilter => {
  const findRFIsInAllStatuses = filterBy.id && !filterBy.status;
  const calculateFilterBy: RFIFilter = {
    ...filterBy,
    ...(findRFIsInAllStatuses && {
      status: findRFIsInAllStatuses ? RfiFilterStatus.All : filterBy.status,
    }),
  };
  const isStatusAllValue =
    calculateFilterBy?.status && calculateFilterBy.status.indexOf(RfiFilterStatus.All) > -1;
  if (isStatusAllValue) {
    // statuses that acceptable by the RFIs API
    calculateFilterBy.status = Object.values(RfiFilterStatus).filter(
      (status) => status !== RfiFilterStatus.All,
    );
  }

  return calculateFilterBy;
};

const getParamList = (filter: Filter): FilterTuple[] => {
  return Object.entries(filter)
    .map(([property, value]) =>
      value !== undefined && !isEmptyArray(value)
        ? getEntityIdOrListFilterTuple(property, value)
        : null,
    )
    .filter((tuple): tuple is FilterTuple => tuple !== null);
};

const handleFetchErrors = async (response: Response) => {
  if (!response.ok) {
    const data = await response.json();
    const status = data.status || response.status;

    const dataWithStatus = {
      ...data,
      ...(status && { status }),
    };
    throw dataWithStatus;
  }
  return response;
};

export const getExtendedFetch = (fetchOptions: FetchOptions) => <T>(
  url: RequestInfo,
  fetchData?: RequestInit,
): Promise<T> => {
  const { getExtendedRequest, onError } = fetchOptions;

  return getExtendedRequest(url, fetchData)
    .then((extendedRequest) => fetch(extendedRequest.url, extendedRequest.fetchData))
    .then(onError)
    .then(handleFetchErrors)
    .then((res) => res.text()) // check if res body exists before trying to json parse it - to avoid parsing errors
    .then((text) => (text ? JSON.parse(text) : {}));
};
